import { notFound } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { municipalityBySlug } from "@/data/municipalities";
import { permanentRedirect } from "@/lib/legacyRedirects";

/**
 * Hjälpfunktioner för dynamiska rutter ($slug): en okänd slug ska ge en riktig
 * 404 (inte en 200-sida som säger "hittades inte"), men först får eventuella
 * omdirigeringar i `url_redirects` chansen att skicka besökaren vidare.
 */

/** Escapar LIKE-tecken så att sökvägen matchas exakt (men skiftlägesokänsligt). */
export const escapeLikePattern = (value: string) =>
  value.replace(/[\\%_]/g, (c) => `\\${c}`);

/**
 * Aktiv omdirigering i databasen för sökvägen, eller null. Matchningen är
 * skiftlägesokänslig eftersom servern gör om versaler i URL:en till gemener
 * (t.ex. /Home/GetResultByQuery -> /home/getresultbyquery) innan rutten körs.
 */
export async function findDbRedirect(path: string): Promise<string | null> {
  const base = supabase
    .from("url_redirects")
    .select("to_path")
    .eq("is_active", true);
  // PostgREST tolkar * som jokertecken i ILIKE – sådana sökvägar matchas exakt.
  const query = path.includes("*")
    ? base.eq("from_path", path)
    : base.ilike("from_path", escapeLikePattern(path));
  const { data, error } = await query.limit(1).maybeSingle();
  if (error) return null;
  const target = (data as { to_path?: string | null } | null)?.to_path ?? null;
  return target && target.toLowerCase() !== path.toLowerCase() ? target : null;
}

/** 301 om databasen har en omdirigering för sökvägen, annars 404. */
export async function notFoundUnlessRedirected(path: string): Promise<never> {
  const target = await findDbRedirect(path);
  if (target) permanentRedirect(target);
  throw notFound();
}

/**
 * Kör en uppslagning av en slug. Ett nätverks- eller databasfel ger
 * `undefined` i stället för ett kastat fel, så att sidan renderas (och hämtar
 * innehållet själv i webbläsaren) i stället för att ge en falsk 404.
 * `null` betyder att uppslagningen lyckades men att slugen inte finns.
 */
export async function lookupSlug<T>(
  lookup: () => Promise<T>,
): Promise<T | undefined> {
  try {
    return await lookup();
  } catch (error) {
    console.error("Uppslagning av slug misslyckades", error);
    return undefined;
  }
}

/**
 * En slug direkt under en tjänstevertikal (t.ex. /solceller/<slug>) finns om
 * den är en kommun (när vertikalen har kommunsidor) eller en publicerad
 * produkt i vertikalens kategori. Annars 404 (eller 301 via url_redirects).
 * Produktuppslagningen görs bara för slugar som inte är kommuner.
 */
export async function assertServiceSlug(
  vertical: string,
  slug: string,
  { municipalityPages }: { municipalityPages: boolean },
): Promise<void> {
  if (municipalityPages && municipalityBySlug(slug)) return;
  const { fetchProductByService } = await import("@/lib/products");
  const product = await lookupSlug(() => fetchProductByService(vertical, slug));
  if (product === null) await notFoundUnlessRedirected(`/${vertical}/${slug}`);
}
